import { z } from "zod";

// Words no organization id may be. The router matches an address by the first
// path segment equal to any route's name, so an organization whose id is one
// of these would have its own `/o/{id}/admin/...` captured by that route, and
// `o` / `w` are the hosting prefixes an organization or wallet address hangs
// off. The legacy tags are the routes' former names, still live as route
// identities and in addresses already saved.
export const RESERVED_ORGANIZATION_IDS: ReadonlySet<string> = new Set([
	"o",
	"w",
	"order",
	"refund",
	"admin-shell",
	"landing",
	"events",
	"new-event",
	"team",
	"locations",
	"ticket-templates",
	"settings",
	"guest-tickets",
	"check-in-events",
	"event",
	"organization",
	"integrations",
	"legal",
	"dashboard",
	"orders",
	"analytics",
	"edit",
	"codes",
	"promotion",
	"check-in",
	"admin",
	"sign-in",
	"storefront",
	"members-view",
	"events-view",
	"locations-view",
	"ticket-templates-view",
	"event-create",
	"event-console-view",
	"settings-view",
	"guest-issue-view",
	"checkin-events-view",
]);

// Addresses encode this existing identity without trimming or changing its case.
export const OrganizationIdSchema = z
	.string()
	.min(1)
	.refine((value) => value.isWellFormed(), "expected valid UTF-8 characters")
	.refine(
		(value) => new TextEncoder().encode(value).byteLength <= 1_500,
		"organization ID exceeds 1,500 UTF-8 bytes",
	)
	.refine(
		(value) => !/[/\\?#%]/.test(value),
		"organization ID must be one address segment",
	)
	.refine(
		(value) => value !== "." && value !== ".." && !/^__.*__$/s.test(value),
		"organization ID is reserved by Firestore",
	)
	.refine(
		(value) => !RESERVED_ORGANIZATION_IDS.has(value),
		"organization ID is reserved by a route",
	);
export type OrganizationId = z.infer<typeof OrganizationIdSchema>;

// These characters cannot survive the deployment environment transport unchanged.
export const DefaultOrganizationIdSchema = OrganizationIdSchema.refine(
	(value) => !/[\r\n\0]/.test(value),
	"default organization ID cannot contain CR, LF or NUL",
);
export type DefaultOrganizationId = z.infer<typeof DefaultOrganizationIdSchema>;
